/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { DownloadUtrvStatus } from '../components/downloads/util/downloadReportHandler';
import { SurveyType } from './survey';
import { UniversalTrackerValuePlain } from './surveyScope';
import { DataPeriods, UniversalTrackerPlain } from './universalTracker';

export enum CustomReportType {
  Metrics = 'metrics',
  Initiatives = 'initiatives',
  Survey = 'survey',
  SurveyAggregation = 'survey_aggregation',
}

export type DateReportType = CustomReportType.Survey | CustomReportType.SurveyAggregation;

export type SurveyData = {
  initiativeIds: string[];
  period: DataPeriods;
  effectiveDate: string; // '2020-01-01'
  statuses?: string[];
  assuranceStatus?: string[];
  type?: SurveyType,
};

export type SurveyDataRange = {
  // Aggregation is restricted to 1 surveyId
  ids: string[];
  statuses?: string[];
  assuranceStatus?: string[];
}

export interface NewCustomReport {
  name: string;
  description?: string;
  status?: DownloadUtrvStatus;
  assuranceStatus?: DownloadUtrvStatus;
}

export interface NewMetrics extends NewCustomReport {
  type: CustomReportType.Metrics,
}

export interface NewInitiativesOrSurvey extends NewCustomReport {
  type: CustomReportType.Initiatives | CustomReportType.Survey,
  survey: SurveyData
}

export interface NewDateComparisonReport extends NewCustomReport {
  type: DateReportType;
  survey: SurveyDataRange;
}

export type CustomReportFormData = NewMetrics | NewInitiativesOrSurvey | NewDateComparisonReport;

export interface CustomReport extends NewCustomReport {
  _id: string;
  initiativeId: string;
  type?: CustomReportType;
  created: string;
  lastUpdated: string;
  metrics: CustomReportMetric[];
  universalTrackers?: UniversalTrackerPlain[];
  survey?: SurveyData | SurveyDataRange;
}

interface Metrics extends CustomReport {
  type: CustomReportType.Metrics | undefined,
  metrics: CustomReportMetric[];
}
export interface CustomInitiatives extends CustomReport {
  type: CustomReportType.Initiatives,
  survey: SurveyData
}
export interface SurveyRange extends CustomReport {
  type: CustomReportType.Survey | CustomReportType.SurveyAggregation,
  survey: SurveyDataRange
}

export type CustomReportBuild = Metrics | CustomInitiatives | SurveyRange;

export interface CustomReportMetric {
  utrId: string;
  type: MetricType;
}

export enum MetricType {
  Single = 'single',
  Calculated = 'calculated',
  Text = 'text'
}

const metricNames: { [key: string]: string } = {
  'single': 'Single metric',
  'calculated': 'Total or % calculation',
  'text': 'Collection of text'
}


export const getMetricTypeLabel = (metricType: MetricType) => metricNames[metricType] ?? 'Unknown metric type';

export type ShowAsTypes = 'total' | 'percentage' | 'text';

export interface MetricSubmitData {
  name: string;
  showAs: ShowAsTypes;
  percentageOf?: 'count' | 'metric';
  primaryUtrCode: string;
  primaryValueListCode: string;
  primaryColumnCode?: string;
  secondaryUtrCode?: string;
  secondaryValueListCode?: string;
  secondaryColumnCode?: string;
}

export interface CustomReportsValue {
  utr: UniversalTrackerPlain,
  utrv: UniversalTrackerValuePlain,
  customMetricType: MetricType;
}

export interface CustomReportSubsidiaryStats {
  _id: string;
  name: string,
  parentId?: string;
  surveyCount: number;
}

export interface CustomReportSubsidiarySurvey {
  _id: string;
  effectiveDate: string;
  period: DataPeriods;
  type?: SurveyType;
}
